import { useMemo, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import { MerchantSelector } from "@/presentation/merchants/components/MerchantSelector";
import { ToolSheetShell } from "./ToolSheetShell";

const client = generateClient<Schema>();

const EVENT_TYPES = [
  "identity_verification_session_verified",
  "identity_verification_session_requires_input",
  "identity_verification_session_canceled",
] as const;
type EventType = (typeof EVENT_TYPES)[number];

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  identity_verification_session_verified:
    "identity.verification_session.verified",
  identity_verification_session_requires_input:
    "identity.verification_session.requires_input",
  identity_verification_session_canceled:
    "identity.verification_session.canceled",
};

const ERROR_CODES = [
  "abandoned",
  "matching_dob_mismatch",
  "matching_name_mismatch",
  "under_supported_age",
  "country_not_supported",
  "document_unverified_other",
  "document_expired",
  "document_type_not_supported",
  "consent_declined",
  "selfie_unverified_other",
  "selfie_document_missing_photo",
  "id_number_unverified_other",
] as const;
type ErrorCode = (typeof ERROR_CODES)[number];

/**
 * What the mobile client does with each code, so the dropdown says which
 * remediation path a simulated event will exercise rather than only its Stripe
 * name. Mirrors `idvResolution` in the mobile app.
 */
const ERROR_CODE_REMEDIATION: Record<ErrorCode, string> = {
  abandoned: "not submitted — plain retry",
  matching_dob_mismatch: "confirm details — edit DOB",
  matching_name_mismatch: "confirm details — edit name",
  under_supported_age: "blocked — no in-app path",
  country_not_supported: "blocked — no in-app path",
  document_unverified_other: "re-capture",
  document_expired: "re-capture",
  document_type_not_supported: "re-capture",
  consent_declined: "re-capture",
  selfie_unverified_other: "re-capture",
  selfie_document_missing_photo: "re-capture",
  id_number_unverified_other: "re-capture",
};

interface FormState {
  businessId: string;
  eventType: EventType;
  errorCode: ErrorCode;
  showVerifiedOverrides: boolean;
  idNumberHash: string;
  dobYear: string;
  dobMonth: string;
  dobDay: string;
  firstName: string;
  lastName: string;
}

const INITIAL: FormState = {
  businessId: "",
  eventType: "identity_verification_session_verified",
  errorCode: "consent_declined",
  showVerifiedOverrides: false,
  idNumberHash: "",
  dobYear: "",
  dobMonth: "",
  dobDay: "",
  firstName: "",
  lastName: "",
};

interface RunState {
  running: boolean;
  result?: unknown;
  error?: string;
}

interface SimulateStripeIdentityEventSheetProps {
  onClose: () => void;
}

export function SimulateStripeIdentityEventSheet({
  onClose,
}: SimulateStripeIdentityEventSheetProps) {
  const isProd = import.meta.env.VITE_HUB_ENVIRONMENT === "production";

  const [form, setForm] = useState<FormState>(INITIAL);
  const [run, setRun] = useState<RunState>({ running: false });

  const formInvalid = useMemo(() => {
    if (!form.businessId.trim()) return true;
    return false;
  }, [form.businessId]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit() {
    setRun({ running: true });
    try {
      const verifiedOverrides =
        form.eventType === "identity_verification_session_verified" &&
        form.showVerifiedOverrides
          ? {
              idNumberHash: form.idNumberHash || undefined,
              dobYear: form.dobYear ? Number(form.dobYear) : undefined,
              dobMonth: form.dobMonth ? Number(form.dobMonth) : undefined,
              dobDay: form.dobDay ? Number(form.dobDay) : undefined,
              firstName: form.firstName || undefined,
              lastName: form.lastName || undefined,
            }
          : undefined;

      const requiresInputErrorCode =
        form.eventType === "identity_verification_session_requires_input"
          ? form.errorCode
          : undefined;

      const result = await client.mutations.simulateStripeIdentityEvent({
        input: {
          businessId: form.businessId.trim(),
          eventType: form.eventType,
          verifiedOverrides,
          requiresInputErrorCode,
        },
      });
      if (result.errors?.length) throw new Error(result.errors[0].message);
      setRun({ running: false, result: result.data });
      toast.success("Event simulated");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setRun({ running: false, error: message });
      toast.error(`Simulation failed: ${message}`);
    }
  }

  return (
    <ToolSheetShell
      title="Simulate Stripe Identity Event"
      onClose={onClose}
      footer={
        <Button
          onClick={handleSubmit}
          disabled={isProd || run.running || formInvalid}
          className="w-full"
        >
          {run.running ? (
            <>
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Simulating…
            </>
          ) : (
            "Simulate Event"
          )}
        </Button>
      }
    >
      {isProd && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          This tool is disabled in production. Use real Stripe events instead.
        </div>
      )}
      <p className="text-muted-foreground text-sm">
        Inject a synthetic identity verification event directly into the
        StripeEvent table. The DDB stream triggers the existing processor
        through the normal handler chain.
      </p>

      <div className="mt-6 space-y-4">
        <Field label="Event type">
          <select
            className="border-border bg-card w-full rounded-md border px-3 py-2 text-sm"
            value={form.eventType}
            onChange={(e) => update("eventType", e.target.value as EventType)}
            disabled={isProd}
          >
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {/* eslint-disable-next-line security/detect-object-injection -- t is always a member of the EVENT_TYPES const array, not user input */}
                {EVENT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Business ID">
          <MerchantSelector
            value={form.businessId}
            onChange={(id) => update("businessId", id)}
            disabled={isProd}
          />
        </Field>

        {form.eventType === "identity_verification_session_requires_input" && (
          <Field label="Error code">
            <select
              className="border-border bg-card w-full rounded-md border px-3 py-2 text-sm"
              value={form.errorCode}
              onChange={(e) => update("errorCode", e.target.value as ErrorCode)}
              disabled={isProd}
            >
              {ERROR_CODES.map((c) => (
                <option key={c} value={c}>
                  {/* eslint-disable-next-line security/detect-object-injection -- c is always a member of the ERROR_CODES const array, not user input */}
                  {`${c} — ${ERROR_CODE_REMEDIATION[c]}`}
                </option>
              ))}
            </select>
          </Field>
        )}

        {form.eventType === "identity_verification_session_verified" && (
          <details
            open={form.showVerifiedOverrides}
            onToggle={(e) =>
              update(
                "showVerifiedOverrides",
                (e.currentTarget as HTMLDetailsElement).open,
              )
            }
            className="border-border rounded-md border p-3"
          >
            <summary className="text-foreground cursor-pointer text-sm font-medium">
              Advanced overrides
            </summary>
            <div className="mt-3 space-y-3">
              <Field
                label="ID number hash"
                hint="Set to the hash of another verified merchant to test dedupe collision detection."
              >
                <Input
                  className="font-mono"
                  value={form.idNumberHash}
                  onChange={(e) => update("idNumberHash", e.target.value)}
                  disabled={isProd}
                />
              </Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="DOB Year">
                  <Input
                    value={form.dobYear}
                    placeholder="1990"
                    onChange={(e) => update("dobYear", e.target.value)}
                    disabled={isProd}
                  />
                </Field>
                <Field label="Month">
                  <Input
                    value={form.dobMonth}
                    placeholder="1"
                    onChange={(e) => update("dobMonth", e.target.value)}
                    disabled={isProd}
                  />
                </Field>
                <Field label="Day">
                  <Input
                    value={form.dobDay}
                    placeholder="1"
                    onChange={(e) => update("dobDay", e.target.value)}
                    disabled={isProd}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="First name">
                  <Input
                    value={form.firstName}
                    onChange={(e) => update("firstName", e.target.value)}
                    disabled={isProd}
                  />
                </Field>
                <Field label="Last name">
                  <Input
                    value={form.lastName}
                    onChange={(e) => update("lastName", e.target.value)}
                    disabled={isProd}
                  />
                </Field>
              </div>
            </div>
          </details>
        )}
      </div>

      {(run.result || run.error) && (
        <div className="border-border mt-6 border-t pt-4">
          {run.error && (
            <pre className="overflow-x-auto rounded bg-red-50 p-3 text-xs text-red-700">
              {run.error}
            </pre>
          )}
          {run.result !== undefined && run.result !== null && (
            <pre className="bg-muted text-foreground overflow-x-auto rounded p-3 text-xs">
              {JSON.stringify(run.result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </ToolSheetShell>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  children: ReactNode;
}

function Field({ label, hint, children }: FieldProps) {
  return (
    <label className="block">
      <span className="text-muted-foreground mb-1 block text-xs font-medium">
        {label}
      </span>
      {children}
      {hint && (
        <span className="text-subtle-foreground mt-1 block text-xs">
          {hint}
        </span>
      )}
    </label>
  );
}
